import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, Clock, MapPin, Send, MessageCircle } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { CONTACT, CITIES, SERVICES } from "@/data/site";

const initial = { name: "", phone: "", city: "", service: "", note: "" };

export default function ContactForm() {
  const [form, setForm] = useState(initial);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.trim().length < 10) {
      toast.error("Please enter your name and a valid 10-digit phone number.");
      return;
    }
    const msg =
      `*New AzoApp Enquiry*%0A%0A` +
      `👤 Name: ${form.name}%0A` +
      `📞 Phone: ${form.phone}%0A` +
      `📍 City: ${form.city || "-"}%0A` +
      `🛠️ Service: ${form.service || "-"}%0A` +
      `📝 Note: ${form.note || "-"}`;
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${msg}`, "_blank");
    toast.success("Opening WhatsApp with your details…");
    setForm(initial);
  };

  const inputCls =
    "w-full rounded-xl border border-azo-border bg-azo-bg px-4 py-3 text-sm outline-none focus:border-azo-primary focus:ring-2 focus:ring-azo-primary/20 transition-all duration-300";

  const infos = [
    { icon: Phone, label: "Call us", value: CONTACT.phoneDisplay, href: `tel:${CONTACT.phone}`, testid: "contact-phone" },
    { icon: MessageCircle, label: "WhatsApp", value: CONTACT.phoneDisplay, href: `https://wa.me/${CONTACT.whatsapp}`, testid: "contact-whatsapp" },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
    { icon: Clock, label: "Hours", value: CONTACT.hours, testid: "contact-hours" },
    { icon: MapPin, label: "Office", value: CONTACT.address, testid: "contact-address" },
  ];

  return (
    <section id="contact" className="relative py-20 sm:py-28 overflow-hidden" data-testid="whatsapp-contact-section">
      <div className="pointer-events-none hidden sm:block absolute -top-24 left-1/2 -translate-x-1/2 h-[420px] w-[720px] rounded-full bg-azo-primary/10 blur-[120px]" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
        {/* left info */}
        <div>
          <Reveal className="flex items-center gap-2 text-azo-primary font-mono2 text-xs uppercase tracking-widest mb-4">
            <span className="h-px w-8 bg-azo-primary" /> 05 — Talk to us
          </Reveal>
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            <MaskedLines lines={["Koi sawaal?", "Hum bas ek", "message door."]} />
          </h2>
          <Reveal delay={0.15} className="mt-5 max-w-md text-azo-muted">
            Form bharo — aapki details seedhe hamare WhatsApp par aayengi aur team turant reply karegi.
          </Reveal>

          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {infos.map((it) => {
              const Comp = it.href ? "a" : "div";
              return (
                <Comp
                  key={it.label}
                  {...(it.href ? { href: it.href, target: it.href.startsWith("http") ? "_blank" : undefined, rel: "noopener noreferrer" } : {})}
                  data-testid={it.testid}
                  className="flex items-start gap-3 rounded-2xl border border-azo-border bg-azo-surface p-4 hover:border-azo-primary transition-colors duration-300"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-azo-primary/12 text-azo-primary">
                    <it.icon size={18} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-mono2 uppercase tracking-widest text-azo-muted">{it.label}</p>
                    <p className="text-sm font-medium break-words">{it.value}</p>
                  </div>
                </Comp>
              );
            })}
          </div>
        </div>

        {/* right form */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl border border-azo-border bg-azo-surface p-6 sm:p-8 shadow-azo"
          data-testid="whatsapp-contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-azo-muted mb-1.5 block">Full name *</label>
              <input value={form.name} onChange={set("name")} placeholder="Aapka naam" className={inputCls} data-testid="whatsapp-form-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-azo-muted mb-1.5 block">Phone number *</label>
              <input value={form.phone} onChange={set("phone")} type="tel" placeholder="10-digit mobile" className={inputCls} data-testid="whatsapp-form-phone-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-azo-muted mb-1.5 block">City</label>
              <select value={form.city} onChange={set("city")} className={inputCls} data-testid="whatsapp-form-city-select">
                <option value="">Select city</option>
                {CITIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-azo-muted mb-1.5 block">Service needed</label>
              <select value={form.service} onChange={set("service")} className={inputCls} data-testid="whatsapp-form-service-select">
                <option value="">Select service</option>
                {SERVICES.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs font-medium text-azo-muted mb-1.5 block">Note (optional)</label>
            <textarea value={form.note} onChange={set("note")} rows={3} placeholder="Apni problem batao…" className={inputCls} data-testid="whatsapp-form-note-input" />
          </div>
          <button
            type="submit"
            data-testid="whatsapp-form-submit-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-azo-primary text-white py-3.5 font-semibold font-heading hover:bg-azo-primaryh hover:-translate-y-0.5 transition-all duration-300 shadow-azo"
          >
            <Send size={16} /> Send on WhatsApp
          </button>
          <p className="mt-3 text-center text-xs text-azo-muted">
            Aapki details WhatsApp par {CONTACT.phoneDisplay} par bheji jayengi.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
